import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { ResolvedRating } from '@/ratings/useRiskRatingResolver';
import { useRiskRatingResolver } from '@/ratings/useRiskRatingResolver';
import { getFriendlyId } from '@/utils/friendlyId';

import type {
  EnterpriseRiskFields,
  EnterpriseRiskRegisterFields,
} from './types';

/**
 * Renders an aggregated likelihood/impact dimension for display. Means and
 * worst-case values land on whole matrix positions; medians can be
 * fractional (the average of the two middle items).
 */
export const formatDimValue = (value: number): string =>
  Number.isInteger(value) ? String(value) : value.toFixed(1);

const dimLabel = (
  value: number | null | undefined,
  resolve: (value: number | null | undefined) => ResolvedRating | undefined
): null | string => {
  if (value == null) {
    return '-';
  }

  return resolve(value)?.label ?? formatDimValue(value);
};

export const useLabelledFields = (
  data: EnterpriseRiskFields[] | undefined
): EnterpriseRiskRegisterFields[] => {
  const { t } = useTranslation(['common']);
  const tiers = useMemo(() => t('tiers', { returnObjects: true }), [t]);
  const treatments = useMemo(
    () => t('treatments', { returnObjects: true }),
    [t]
  );
  const { getByValue: getResidualRating } = useRating('risk_controlled');
  const { getByValue: getInherentRating } = useRating('risk_uncontrolled');
  const { resolveLikelihood, resolveImpact } = useRiskRatingResolver(
    'standard',
    { configType: 'risk_assessment' }
  );

  return useMemo(() => {
    return (
      data?.map((record) => ({
        ...record,
        SequentialIdLabelled: record.SequentialId
          ? getFriendlyId(Parent_Type_Enum.EnterpriseRisk, record.SequentialId)
          : '-',
        TierLabelled: tiers[String(record.Tier) as keyof typeof tiers],
        TreatmentLabelled:
          treatments[String(record.Treatment) as keyof typeof treatments],
        ParentTitle: record.parent?.Title ?? '-',
        CreatedByUser: record.createdByUser?.FriendlyName ?? '-',
        ModifiedByUser: record.modifiedByUser?.FriendlyName ?? '-',
        Description: record.Description ?? '-',
        InherentMeanLabelled:
          getInherentRating(record.score?.InherentRatingMean)?.label ?? '-',
        InherentWorstCaseLabelled:
          getInherentRating(record.score?.InherentRatingWorstCase)?.label ??
          '-',
        ResidualMeanLabelled:
          getResidualRating(record.score?.ResidualRatingMean)?.label ?? '-',
        ResidualWorstCaseLabelled:
          getResidualRating(record.score?.ResidualRatingWorstCase)?.label ??
          '-',
        InherentLikelihoodMeanLabelled: dimLabel(
          record.score?.InherentLikelihoodMean,
          resolveLikelihood
        ),
        InherentImpactMeanLabelled: dimLabel(
          record.score?.InherentImpactMean,
          resolveImpact
        ),
        ResidualLikelihoodMeanLabelled: dimLabel(
          record.score?.ResidualLikelihoodMean,
          resolveLikelihood
        ),
        ResidualImpactMeanLabelled: dimLabel(
          record.score?.ResidualImpactMean,
          resolveImpact
        ),
        InherentLikelihoodMedianLabelled: dimLabel(
          record.score?.InherentLikelihoodMedian,
          resolveLikelihood
        ),
        InherentImpactMedianLabelled: dimLabel(
          record.score?.InherentImpactMedian,
          resolveImpact
        ),
        ResidualLikelihoodMedianLabelled: dimLabel(
          record.score?.ResidualLikelihoodMedian,
          resolveLikelihood
        ),
        ResidualImpactMedianLabelled: dimLabel(
          record.score?.ResidualImpactMedian,
          resolveImpact
        ),
        InherentLikelihoodWorstCaseLabelled: dimLabel(
          record.score?.InherentLikelihoodWorstCase,
          resolveLikelihood
        ),
        InherentImpactWorstCaseLabelled: dimLabel(
          record.score?.InherentImpactWorstCase,
          resolveImpact
        ),
        ResidualLikelihoodWorstCaseLabelled: dimLabel(
          record.score?.ResidualLikelihoodWorstCase,
          resolveLikelihood
        ),
        ResidualImpactWorstCaseLabelled: dimLabel(
          record.score?.ResidualImpactWorstCase,
          resolveImpact
        ),
      })) ?? []
    );
  }, [
    data,
    tiers,
    treatments,
    getInherentRating,
    getResidualRating,
    resolveLikelihood,
    resolveImpact,
  ]);
};
